import { useCallback } from 'react';
import { apiFetchBlob } from '../../api/client';
import { useAccessToken } from '../../auth/useAccessToken';
import { useAlert } from '../../components/dialogs/DialogProvider';
import { buildExportFilename } from '../../utils/export';

/**
 * Server-side revenue export (task 11 — spec §11: export is generated on
 * the server; the browser only downloads the bytes the server produced,
 * never builds CSV/PDF client-side here — that's `utils/export.ts`'s job
 * for grid-backed pages, not the revenue dashboard). Backs the Export CSV /
 * Export PDF buttons on all three revenue pages (portfolio / site /
 * machine), each hitting the matching Task-7 endpoint:
 *
 *   GET /api/v1/revenue/portfolio/export.{csv,pdf}
 *   GET /api/v1/revenue/sites/{siteId}/export.{csv,pdf}
 *   GET /api/v1/revenue/machines/{machineId}/export.{csv,pdf}
 *
 * with the SAME `period`/`from`/`to` query params the read endpoints take.
 */
export type RevenueExportView = 'portfolio' | 'sites' | 'machines';
export type RevenueExportFormat = 'csv' | 'pdf';

const ENTITY_NAME: Record<RevenueExportView, string> = {
  portfolio: 'revenue-portfolio',
  sites: 'revenue-site',
  machines: 'revenue-machine',
};

/**
 * Mirrors the private `periodQs` in `api/revenue.ts` — kept as its own
 * tiny copy here rather than exported/shared so this file only touches the
 * export surface, not the read-query module `useRevenueMachine` et al
 * already ship. `from`/`to` only go on the wire for `custom` — same
 * `invalid_period` 400 the read endpoints guard against otherwise.
 * `operatorId`/`siteId` mirror the portfolio view's filter-bar drills (parity
 * with the screen — RevenueEndpoints.cs runs the export through the SAME
 * Get*Async the screen uses, so a filtered screen must export the filtered
 * data, not the unfiltered portfolio).
 */
function periodQs(
  period: string, from?: string, to?: string, operatorId?: string, siteId?: string,
): string {
  const p = new URLSearchParams({ period });
  if (period === 'custom' && from && to) {
    p.set('from', from);
    p.set('to', to);
  }
  if (operatorId) p.set('operatorId', operatorId);
  if (siteId) p.set('siteId', siteId);
  return p.toString();
}

function exportPath(
  view: RevenueExportView,
  id: string | null,
  fmt: RevenueExportFormat,
  period: string,
  from?: string,
  to?: string,
  operatorId?: string,
  siteId?: string,
): string {
  const base = view === 'portfolio' ? '/api/v1/revenue/portfolio' : `/api/v1/revenue/${view}/${id}`;
  return `${base}/export.${fmt}?${periodQs(period, from, to, operatorId, siteId)}`;
}

/** Model of `utils/export.ts`'s `triggerDownload` — that one isn't exported. */
function triggerDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Returns a stable callback — `(view, id, period, from, to, fmt, operatorId?,
 * siteId?) => Promise<void>` — that fetches the server-generated export and
 * triggers a browser download. `id` is `null` for the portfolio view (no
 * route param) and required for `sites`/`machines`; a missing required id is
 * a no-op (the caller's button should already be `disabled` in that state —
 * e.g. before the page's drill-down data has loaded). The trailing
 * `operatorId`/`siteId` are the portfolio view's filter-bar drills — pass the
 * screen's current selection so the export matches what's on screen (parity;
 * unused / ignored server-side for `sites`/`machines`). Errors (network /
 * non-2xx) are never swallowed — they surface via the themed `useAlert`
 * dialog per the house "errors visible in the UI" rule.
 */
export function useRevenueExport() {
  const getToken = useAccessToken();
  const alert = useAlert();

  return useCallback(
    async (
      view: RevenueExportView,
      id: string | null,
      period: string,
      from: string | undefined,
      to: string | undefined,
      fmt: RevenueExportFormat,
      operatorId?: string,
      siteId?: string,
    ): Promise<void> => {
      if (view !== 'portfolio' && !id) return;
      try {
        const path = exportPath(view, id, fmt, period, from, to, operatorId, siteId);
        const blob = await apiFetchBlob(path, getToken);
        triggerDownload(blob, buildExportFilename(ENTITY_NAME[view], id ?? '', fmt));
      } catch (e) {
        await alert({
          title: 'Export failed',
          message: (e as { message?: string })?.message ?? 'Failed to export revenue data.',
          severity: 'error',
        });
      }
    },
    [getToken, alert],
  );
}
